/**
 * Runtime browse mode for logged-out access.
 *
 * OPEN — token-optional public browse/search/detail for public resources.
 * CLOSED — legacy production behavior (share tokens and auth gates unchanged).
 */

export const PLATFORM_BROWSE_MODES = ['open', 'closed'] as const;

export type PlatformBrowseMode = (typeof PLATFORM_BROWSE_MODES)[number];

/** Server default when browse_mode is unset after a successful config fetch. */
export const DEFAULT_PLATFORM_BROWSE_MODE: PlatformBrowseMode = 'open';

/**
 * Safe client fallback when public config cannot be loaded.
 * Avoids accidentally broadening guest access on transient API failures.
 */
export const FAILSAFE_PLATFORM_BROWSE_MODE: PlatformBrowseMode = 'closed';

export const PLATFORM_BROWSE_MODE_CHANGED_EVENT = 'gt:platform-browse-mode-changed';

export function isPlatformBrowseMode(value: unknown): value is PlatformBrowseMode {
  return value === 'open' || value === 'closed';
}

export function normalizePlatformBrowseMode(value: unknown): PlatformBrowseMode {
  return isPlatformBrowseMode(value) ? value : DEFAULT_PLATFORM_BROWSE_MODE;
}

export function isOpenBrowseMode(mode: PlatformBrowseMode): boolean {
  return mode === 'open';
}

export function isClosedBrowseMode(mode: PlatformBrowseMode): boolean {
  return mode === 'closed';
}

/** Routes that become public only when browse mode is OPEN. */
export function isOpenBrowsePublicPath(pathname: string): boolean {
  // /results hosts destination & travel-style browse inventory for guests in OPEN.
  // Freeform search-bar entry points stay auth-gated in UI; this path stays public for card browse.
  return pathname === '/results' || pathname.startsWith('/recommendation/');
}

export function guestRequiresShareTokenForPublicRead(
  browseMode: PlatformBrowseMode,
  hasUser: boolean,
  token: string | null | undefined
): boolean {
  if (hasUser) return false;
  if (token) return false;
  return isClosedBrowseMode(browseMode);
}

/** Backend routes under /api/v1/public/* or /api/v2/public/* (token-optional reads). */
export function isPublicApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.includes('/api/v1/public/') || lower.includes('/api/v2/public/');
}

/** Guest post browse/search in OPEN mode uses GET /api/v2/public/posts/post_types. */
export function shouldUsePublicPostsSearchEndpoint(
  hasAuth: boolean,
  browseMode: PlatformBrowseMode | undefined
): boolean {
  return shouldUseOpenBrowseGuestEndpoints(hasAuth, browseMode, false);
}

/**
 * Guest OPEN browse uses OPEN-gated /api/v2/public/* routes (e.g. posts/post_types),
 * not legacy logged-out landing teasers (featured-posts, featured-creators).
 */
export function shouldUseOpenBrowseGuestEndpoints(
  hasAuth: boolean,
  browseMode: PlatformBrowseMode | undefined,
  isBrowseModeLoading: boolean
): boolean {
  if (hasAuth || isBrowseModeLoading || browseMode === undefined) return false;
  return isOpenBrowseMode(browseMode);
}

/** Legacy logged-out landing endpoints — used for guests in CLOSED mode (or while browse mode loads). */
export function shouldUseLegacyLandingPublicEndpoints(
  hasAuth: boolean,
  browseMode: PlatformBrowseMode | undefined,
  isBrowseModeLoading: boolean
): boolean {
  if (hasAuth) return false;
  if (isBrowseModeLoading || browseMode === undefined) return true;
  return isClosedBrowseMode(browseMode);
}

/**
 * Skip authenticated-only guest API calls when browse is CLOSED or config is still loading.
 * OPEN guests should use OPEN-gated /api/v2/public/* routes instead.
 */
export function shouldSkipGuestAuthenticatedApiCalls(
  hasAuth: boolean,
  browseMode: PlatformBrowseMode | undefined,
  isBrowseModeLoading = false
): boolean {
  if (hasAuth) return false;
  if (isBrowseModeLoading || browseMode === undefined) return true;
  return isClosedBrowseMode(browseMode);
}

/** OPEN browse hides profile metrics when viewing someone else's profile. */
export function shouldHideOtherProfileMetrics(
  browseMode: PlatformBrowseMode | undefined,
  isOwnProfile: boolean
): boolean {
  if (isOwnProfile) return false;
  return isOpenBrowseMode(browseMode ?? FAILSAFE_PLATFORM_BROWSE_MODE);
}
